import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound } from "lucide-react";
import { useLang } from "@/i18n";

export const scrollToSection = (href) => (e) => {
  if (e) e.preventDefault();
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-white/15" data-testid="lang-toggle">
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-mono transition-colors duration-300 ${
            lang === l ? "bg-[#D4AF37] text-[#0A0A0C]" : "text-zinc-400 hover:text-[#D4AF37]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#operazioni", label: t("nav_operations"), testid: "nav-link-operations" },
    { href: "#approccio", label: t("nav_approach"), testid: "nav-link-manifesto" },
    { href: "#numeri", label: t("nav_numbers"), testid: "nav-link-numbers" },
    { href: "#contatti", label: t("nav_contact"), testid: "nav-link-contact" },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#0A0A0C]/85 backdrop-blur-xl border-b border-amber-500/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between h-[72px]">
        <a
          href="#"
          data-testid="nav-logo"
          onClick={(e) => {
            e.preventDefault();
            window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo.png"
            alt="Capital Stone — Investimenti Immobiliari"
            className="h-10 sm:h-11 w-auto transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              onClick={scrollToSection(l.href)}
              className="text-xs tracking-[0.22em] uppercase text-zinc-400 hover:text-[#D4AF37] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <LangToggle />
          <Link
            to="/admin"
            data-testid="nav-admin-login-btn"
            className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-zinc-300 border border-amber-500/25 px-4 py-2 hover:bg-amber-500/10 hover:border-amber-500/50 transition-colors duration-300"
          >
            <KeyRound size={13} className="text-[#D4AF37]" />
            {t("nav_admin")}
          </Link>
        </nav>

        <div className="md:hidden flex items-center gap-3">
          <LangToggle />
          <Link
            to="/admin"
            data-testid="nav-admin-login-btn-mobile"
            className="text-zinc-300 border border-amber-500/25 p-2"
          >
            <KeyRound size={16} className="text-[#D4AF37]" />
          </Link>
        </div>
      </div>
    </motion.header>
  );
};
